import Backbone from 'backbone'
import _ from 'lodash'
import template from './field-value-control.hbs'
import controlsCheckbox from './controls-checkbox.hbs'
import controlsText from './controls-text.hbs'
import controlsRollup from './controls-rollup.hbs'
import controlsCombo from './controls-combo.hbs'
import controlsValue from './controls-value.hbs'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import DateInput from 'scoreboard/components/date-input'
import selects from 'scoreboard/components/selects'
import MarkedField from 'scoreboard/components/marked-field'
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import numericValidator from 'scoreboard/components/validators/numeric-validator'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import formHelper from 'scoreboard/components/form-helper'
import KeyDisplay from 'scoreboard/datasets/key-display'
import disableControls from 'scoreboard/components/disable-controls'
import DatasetTreeItemSelector from './dataset-tree-item-selector'
import appDisplay from 'scoreboard/components/app-display'
import RecordUtil from 'scoreboard/datasets/records/record-util'
const { isNewRecord, isSupplementalOnly } = RecordUtil
import Tooltip from 'scoreboard/components/tooltip'
import i18n from 'scoreboard/components/i18n'
import TimeInput from 'scoreboard/components/time-input'
import moveDecimal from 'scoreboard/components/move-decimal'
import formatValue from 'scoreboard/components/format-value'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import $ from 'jquery'
import * as DatasetFieldFilter from 'scoreboard/forms/dataset-field-filter'
const { isJunctionLinkField } = DatasetFieldFilter
import FieldValueFormatter from 'scoreboard/datasets/records/field-value-formatter'
import dropdown from 'scoreboard/components/dropdown'
import calendars from 'scoreboard/components/calendars'
import isGeographicOrRollup from '../is-geographic-or-rollup'
import moment from 'moment'
import momentHelper from 'scoreboard/components/moment-helper'
import isGeographic from 'scoreboard/components/is-geographic-data-type'
import { dataTypeToTreeType } from 'scoreboard/components/tree-type-to-data-type'
import WidgetState from 'scoreboard/forms/model/widget-state'
const { TEXT_ONLY, DISABLED, HIDDEN } = WidgetState
import LazyFilteringSelect from 'scoreboard/components/lazy-filtering-select'

import DataType from 'scoreboard/datasets/model/data-type'
const { PERCENT, TEXT, YES_NO, DATE, TIME, DATE_TIME, CALENDAR_PERIOD, DATASET_LINK, CURRENCY,  } = DataType

import { to24hrTime, ISO_8601_REGEX } from '../../components/date-time-util'

const FieldValueControl = Backbone.View.extend({

	className: 'field-value-control',

	events: {
		'click .select-tree-item': 'selectTreeItem',
		'click .clear-tree-item': 'clearTreeItem',
		// #56793 an input/textarea fires a 'change' event when it's
		// unfocused on after its value has changed. Events should be fired
		// immediately when a user actually inputs data into these elements so
		// only fire a change event on the select element
		'change select': 'modifyInput',
		'keyup input, textarea': 'modifyInput',
		// #12784 date controls fire input events
		'input input': 'modifyInput',
		'click .undo': 'undo',
		'click .not-editable-pk-value': 'onClickNotEditablePkInput'
	},

	/**
	 * A control appropriate for editing the given field
	 *
	 * @constructs
	 * @property {Number} fieldIndex the index of this control's field within
	 * the record
	 * @property {DatasetRecord} record the record currently being edited, if
	 * applicable
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model
	 *
	 * @param {FormView} options.formView
	 *
	 * @param {Number} options.fieldId the ID of the field being edited
	 *
	 * @param {Object} [options.suggestions] arrays of suggestions keyed by
	 * fieldId
	 *
	 * @param {Object} [options.linkFieldSuggestions] arrays of suggestions for
	 * linked fields keyed by fieldId
	 *
	 * @param {Object} [options.value] the initial value for the control
	 *
	 * @param {Boolean} [options.required=false] whether the field is required.
	 * This applies for fields other than the primary key. The primary key is
	 * always required
	 *
	 * @param {Object<Number,Object>} options.treeItemsById is a hash where keys are
	 * tree IDs and values are tree items
	 *
	 * @param {Object} [options.allowInput=true] indicates whether the widget
	 * is allowing data to be entered
	 *
	 * @param {Boolean} [options.enableEditUndo=true] indicates whether to
	 * include the EDITED/UNDO functionality. If the record is manually added,
	 * this setting is ignored and the EDITED/UNDO functionality won't be
	 * present (#34668). The functionality will also be hidden when
	 * allowInput=false
	 *
	 * @param {DatasetRecord} options.record the record currently being edited
	 *
	 * @param {String} [options.label] an optional label to apply to the control
	 *
	 * @param {String} [options.description] an optional description to apply to
	 * the control
	 *
	 * @param {Boolean} [options.renderLinkedFieldAsSelect] if true and the field
	 * for this control has a 'dataset-link' data type, the control will be rendered
	 * as a filtering select. If false, a normal text box is used
	 * @param {Boolean} [options.allowYesNoBlankOption=true] Determines if a blank
	 * dropdown select should be populated for Yes/No fields
	 *
 	 * @param {Boolean} [options.enableLazySelect=false] Determines if a select
	 * should start as a native select and be converted to a rich select on
	 * click.
	 */
	initialize: function(options = {}) {
		this.options = options
		this.multiline = this.options.multiline
		this.field = this.model.combinedFieldsById[this.options.fieldId]
		this.underlyingField = this.model.getUnderlyingField(this.options.fieldId)
		this.required = _.get(options, 'required', false)
		this.record = this.options.record || {}
		this.textOnly = this.options.state === TEXT_ONLY // must be before initEnableEditUndo
		this.initAllowInput() // must be after this.field and this.record init
		this.initEnableEditUndo() // must be after this.initAllowInput
		this.initValue() // must be after this.initAllowInput
		this.formView = this.options.formView
		this.fieldIndex = _.findIndex(this.model.get('fields'), field =>
			field?.id == this.options?.fieldId)

		// Default to true since this is the default behavior for most of the app
		this.allowYesNoBlankOption = this.options.allowYesNoBlankOption ?? true
		// #58570 debounce the change event so that we don't fire too many
		// evaluate-statement requests to the server
		this.change = _.debounce(() => this.trigger('change'), 250)
	},

	initAllowInput: function() {
		this.allowInput = _.get(this.options, 'allowInput', true)
		if (isJunctionLinkField(this.field, this.model)) {
			// If this is a junction linked field, we only need to ensure that the
			// junction dataset allows adding/removing records
			const junctionLink = _.find(this.model.get('junctionDatasets'), d => {
				return d.datasetId == this.model.id && d.linkedDatasetId == this.field.datasetId
			})
			const junctionDataset = this.model.datasetsById[junctionLink.junctionDatasetId]
			this.allowInput = junctionDataset.get('newAllowed') || false
			if (!this.allowInput) this.textOnly = true
			return
		}

		// #34631 also disable the controls if the field is not editable
		if (!_.get(this.field, 'editable') && !isSupplementalOnly(this.record)) {
			this.allowInput = false
		}
		// #35005 not allowed to edit the primary key for records in the source
		// data
		if (this.isNotEditablePk()) {
			this.allowInput = false
		}
		// #35460 don't allow input on the Record Manually Added Field
		if (this.isManuallyAddedField() ||
			// #39565 forms should account for modify records permission
			!this.model.isModifyRecords() ||
			// #42093 don't allow input on calculated fields, unless this is a dataset
			// with no data source
			(this.isCalculatedField() && !this.model.hasNoDataSource())) {
			this.allowInput = false
		}
		if (_.includes([DISABLED, HIDDEN], this.options.state)) this.allowInput = false
	},

	/**
	 * this.enableEditUndo needs to be set here, and ONLY here so that when the
	 * template is rendered it is not included in the DOM when it is hidden.
	 * This prevents the `click .undo` event from firing and fixes #36209
	 */
	initEnableEditUndo: function() {
		this.enableEditUndo = _.get(this.options, 'enableEditUndo', true)
		// #34668 edited/undo resets the data to the value from the source data
		// but that doesn't even make sense if there is no source data
		if ((this.options.record && isSupplementalOnly(this.options.record)) ||
			!this.allowInput || // #35090
			isNewRecord(this.record) ||
			this.textOnly
		) {
			this.enableEditUndo = false
		}
	},

	initValue: function() {
		let value = this.options.value
		// #40586 removed part of changes for #38025 - moving the decimal here
		// had the side effect of display value x 100 when editing
		// (in dataset records and forms)

		// if displaying a text-only dataset link, find the value of the
		// Value Label Field through the suggestions
		if (this.isDatasetLink() && this.textOnly && !_.isNil(value)) {
			const suggestion = _.find(this.options.linkFieldSuggestions, lfs =>
				lfs.id == value)
			if (!_.isNil(suggestion)) {
				value = suggestion.suggestion
			}
		}
		this.value = value
	},

	isManuallyAddedField: function() {
		return this.model.isManuallyAddedFieldId(_.get(this.field, 'id'))
	},

	isCalculatedField: function() {
		return this.field.fieldType === 'calculated'
	},

	render: function() {
		this.$el
			.empty()
			.attr('data-id', this.field.id)
			.data('control', this)
		if (this.field) {
			this.renderTemplate()
			// after rendering the template
			this.initializeEdited()
			this.writeToDom()
			this.handleAllowInput()
			this.renderFormElement()
			// must go after rendering this.markedField
			this.handleNotEditablePk()
		}

		// #48397 stop propagation on mouseup events for the case that this
		// control is inside of a record list
		this.$el.on('mouseup', e => e.stopPropagation())
		return this
	},

	// initialize the edited and undo buttons, and the undo all edits button
	initializeEdited: function() {
		if (!this.enableEditUndo) return

		if (!isNewRecord(this.record)) {
			let edited = this.record.edited[this.fieldIndex]
			this.toggleEdited(edited)
		}
	},

	/** Renders an HTML template appropriate for the control */
	renderTemplate: function() {
		let controls
		let dataType = this.underlyingField.dataType
		let context = _.extend({}, this.field, {
			multiline: this.multiline,
			label: _.isEmpty(this.options.label) ? this.field.label : null
		})
		// We check this first because any non-checkbox text-only widgets get the value control
		if (this.textOnly && !this.options.checkbox) {
			const value = this.getTextOnlyValue(dataType)
			controls = controlsValue({ value })

		// We check this next because it is looking at a different data type field
		// #46730 unless we are dealing with a  rollup field
		} else if (this.isDatasetLink() && !isGeographicOrRollup(dataType)) {
			const template = this.allowInput ? controlsCombo : controlsText
			controls = template(context)
		} else if (dataType === CALENDAR_PERIOD) {
			controls = controlsCombo(context)
		} else if (dataType === YES_NO) {
			if (this.options.checkbox) {
				controls = controlsCheckbox(this.field)
			} else {
				controls = controlsCombo(this.field)
			}
		} else if (isGeographicOrRollup(dataType)) {
			controls = controlsRollup(this.field)

			// Numeric fields can be combo inputs as they can take suggestions
		} else if (dataType === TEXT || isNumericDataType(dataType)) {
			if (_.isEmpty(_.get(this.options.suggestions, this.field.id)) || this.multiline) {
				controls = controlsText(context)
			} else {
				controls = controlsCombo(context)
			}
		} else {
			controls = controlsText(context)
		}

		let description = this.options.description

		// indicates we're displaying a label, description or edit/undo
		// functionality
		let extraInfo = this.options.label || description ||
			this.enableEditUndo

		const isTextOnlyCheckbox = this.textOnly && this.options.checkbox
		const label = this.options.label
		this.$el.html(template({
			id: this.options.fieldId,
			label: isTextOnlyCheckbox ? `${label}:` : label,
			description,
			enableEditUndo: this.enableEditUndo,
			checkbox: this.options.checkbox,
			extraInfo,
			controls
		}))

		if (isTextOnlyCheckbox) {
			this.$('.label-text').after(controlsValue({
				value: this.getTextOnlyValue() }))
		}
	},

	getTextOnlyValue: function(dataType) {
		let value
		const selections = this._getSelectedLinkRecords()
		if (this.isDatasetLink() && selections) {
			// #46020 Values linked by a junction dataset have to be gathered
			// separately of this.value
			value = selections
				? _.join(_.map(selections, s => escapePreserveLineBreaks(s.suggestion)), ', ')
				: null
		} else {
			const fieldValueFormatter = new FieldValueFormatter(this.model,
				this.options.treeItemsById)
			// #56133
			// The ISO regex match is to ensure we don't format a value set
			// through set-field-value-actor.js
			if (dataType === TIME && !ISO_8601_REGEX.test(this.value)) {
				value = to24hrTime(this.value)
				value = fieldValueFormatter.format(this.field, value)
			} else {
				value = fieldValueFormatter.format(this.field, this.value)
			}
		}
		return value
	},

	isDatasetLink: function() {
		return this.options.renderLinkedFieldAsSelect
			&& (this.field.dataType === DATASET_LINK || isJunctionLinkField(this.field, this.model))
	},

	writeToDom: function() {
		let data = {}
		data[this.field.id] = this.value
		formHelper.writeToDom(data, this.el)

		let underlyingField = this.model.getUnderlyingField(this.field)
		if (isGeographicOrRollup(underlyingField.dataType)) {
			this.setRollupValue()
		}
	},

	handleAllowInput: function() {
		if (!this.allowInput && !this.isNotEditablePk()) {
			disableControls(this.el)
		}
	},

	/**
	 * Returns true if the field is for the primary key of the main dataset and
	 * it isn't editable since the record wasn't manually added
	 */
	isNotEditablePk: function() {
		return this.field.datasetId == this.model.id
			&& this.field.primaryKey && !isSupplementalOnly(this.record)
	},

	handleNotEditablePk: function() {
		if (!this.isNotEditablePk()) {
			return
		}

		let el = this.$(disableControls.selector)
		el.attr('disabled', 'false')
			.addClass('is-disabled')
		this.markedField &&
			this.markedField.$el.addClass('not-editable-pk-value') &&
			this.markedField.disable()
	},

	onClickNotEditablePkInput: function(e) {
		_.remove(this.notEditablePkTooltip, 'remove')
		this.notEditablePkTooltip = new Tooltip({
			attachTo: e.target,
			content: i18n.get('datasets.primaryKey.notEditable.manuallyAddedOnly')
		}).render()
	},

	/**
	 * Renders the actual form element the user will interact with (
	 * input, select, textarea, etc.)
	 */
	renderFormElement: function() {
		let dataType = this.underlyingField.dataType
		if (this.isDatasetLink() && !isGeographicOrRollup(dataType)) {
			// #41273 handle dataset links first as it's not based on the underlying
			// field #46730 except for rollup fields
			this.renderLinkedDatasetControl()
			return
		}
		if (dataType === TEXT || isNumericDataType(dataType)) {
			this.renderSuggestions() // needs to go before creating the marked field below
			this.renderNumericOrTextControl()
		}
		if (dataType === DATE) {
			this.renderDateControl()
		}
		if (dataType === DATE_TIME) {
			this.renderDateTimeControl()
		}
		if (dataType === TIME) {
			this.renderTimeControl()
		}
		if (dataType === YES_NO) {
			this.renderYesNoControl()
		}
		if (isGeographicOrRollup(dataType)) {
			this.renderRollupControl()
		}
		if (dataType === CALENDAR_PERIOD) {
			this.renderCalendarPeriodControl()
		}
	},

	_getSelectedLinkRecords: function() {
		const suggestions = this.options.linkFieldSuggestions
		const linkedRecords = this.record.linkedRecords || {}
		const links = _.get(linkedRecords, `[${this.field.id}]`)
		if (!links) return null

		let selections
		if (_.isEmpty(suggestions)) {
			selections = []
		} else {
			selections = _.filter(suggestions, s =>
				links.includes(s.id) || this.value == s.id)
		}
		return selections
	},

	renderLinkedDatasetControl: function() {
		const suggestions = this.options.linkFieldSuggestions
		const selections = this._getSelectedLinkRecords() || []
		const treeItemsById = this.options.treeItemsById
		const dataType = this.options.valueLabelFieldId
			? this.model.combinedFieldsById[this.options.valueLabelFieldId].dataType
			: this.field.dataType
		const options = _.map(suggestions, sugg => {
			const selected = _.some(selections, s => s.id == sugg.id)
				|| sugg.id == this.value

			let label = sugg.suggestion
			if (isGeographicOrRollup(dataType) && treeItemsById[label]) {
				label = treeItemsById[label].label
			}
			if (dataType === DATE) {
				label = moment(label).format(momentHelper.numericDateFormat)
			}
			return { value: sugg.id, label, selected }
		})

		// create the select

		// if this field does not belong to this dataset, then we are editing a
		// many-to-one relationship and need to be able to select multiple values
		const multiple = this.field.datasetId !== this.model.get('id')
		this.fieldEl = this.$('[name=' + this.field.id + ']')
		this.fieldEl.prop('disabled', this.options.state === DISABLED)
		const selectOptions = { options, multiple }

		// #45709 don't show the placeholder if input is not allowed
		if (!this.allowInput) selectOptions.placeholder = ''

		selects.filtering(this.fieldEl, selectOptions)
		this.fieldEl.data('val', this._val.bind(this))
		// #59356 add marked field if input is required
		// (The only validator available for dataset links is the required
		// validator, so we don't need to provide the data type here)
		let validators = this.getValidators()
		this.markedField = new MarkedField({
			el: this.fieldEl,
			validator: this.allowInput ? validators : null,
		}).render()
	},

	/** Put the primary key in a required marked-field, and create combo boxes
	if there are any suggestions for the text field */
	renderNumericOrTextControl: function() {
		let validators = this.getValidators()
		let el = this.$('[name=' + this.field.id + ']')
		el.prop('disabled', this.options.state === DISABLED)

		let dataType = this.underlyingField.dataType
		this.markedField = new MarkedField({
			el: el[0],
			evaluator: isNumericDataType(this.underlyingField.dataType) ?
				val => numericEvaluator(this._stripPercentSign(val)) : null,

			// If input is not allowed, the field will be disabled anyway
			// and we don't want to require input from a disabled field
			validator: this.allowInput ? validators : null,
			// If this is a 'text' control, pass in null so the markedField
			// renders properly #37970
			dataType: dataType == TEXT ? null : dataType,
				// #42971 pass along currencyCode for markedField marker content
				currencyCode: this.underlyingField.currencyCode
			}).render()

	},

	_stripPercentSign: function(input) {
		if (this.underlyingField.dataType !== PERCENT || typeof input !== 'string') return input
		// #56520 - Remove leading (Arabic) or trailing (English) percentages
		return input && input.replace(/^[%٪]|[%٪]$/, '')
	},

	/** render date inputs */
	renderDateControl: function() {
		let validator
		if (this.allowInput) {
			validator = this.getValidators()
		}
		this.markedField = new DateInput({
			el: this.$('[name=' + this.field.id + ']'),
			validator
		}).render()
	},

	renderDateTimeControl: function() {
		let validators = this.allowInput ? this.getValidators() : null
		this.markedField = new TimeInput({
			isDateTime: true,
			hasCalendarControl: true,
			validators,
			el: this.$('[name=' + this.field.id + ']'),
			timeDisplayType: this.underlyingField.timeDisplayType
		}).render()
	},

	renderTimeControl: function() {
		let validators = this.allowInput ? this.getValidators() : null
		this.markedField = new TimeInput({
			validators,
			el: this.$('[name=' + this.field.id + ']'),
			timeDisplayType: this.underlyingField.timeDisplayType
		}).render()
	},

	/** render yes/no select */
	renderYesNoControl: function() {
		if (this.options.checkbox) {
			return
		}
		let validators = this.getValidators()
		let yesNoSelect = this.$('[name=' + this.field.id + ']')
		if (!_.isEmpty(validators) && this.allowInput) {
			this.markedField = new MarkedField({
				el: yesNoSelect,
				validators
			}).render()
		}
		let options = [
			{ label: i18n.get('common.yes'), value: true },
			{ label: i18n.get('common.no'), value: false }
		]
		if (this.allowYesNoBlankOption) {
			options.unshift({ label: '', value: '' })
		}
		selects.filtering(yesNoSelect, {
			selected: _.isNil(this.value) ? '' : `${this.value}`,
			options,
			placeholder: this.options.label ? '' : this.field.label
		})
	},

	renderRollupControl() {
		let fieldEl = this.$('[name=' + this.field.id + ']')
		let CustomMarkedField = MarkedField.extend({
			val: this.getRollupValue.bind(this),
			_val: this.getRollupValue.bind(this)
		})
		this.markedField = new CustomMarkedField({
			el: fieldEl,
			validators: this.allowInput ? this.getValidators() : null,
			dataType: this.field.dataType
		}).render()
	},

	renderCalendarPeriodControl: function() {
		let fieldEl = this.$('[name=' + this.field.id + ']').empty()
		let calendar = calendars.calendarsById[this.field.defaultCalendarId]
		// pass null to dropdown.idLabels instead of undefined, so no value is pre-populated
		const value = !_.isNil(this.value) ? this.value : null
		dropdown.idLabels(fieldEl, calendar.periods, value)
	},

	setRollupValue(opts = {}) {
		const dataType = this.underlyingField.dataType
		const val = !_.isNil(opts.value) ? opts.value : this.value
		this.$('[name=' + this.field.id + ']').val(val)
		let label = this.options.label ? '' : this.field.label
		if (val) {
			label = new KeyDisplay({
				model: this.model,
				field: this.field,
				treeItemsById: this.options.treeItemsById
			}).labelAndKeys(val)
		} else {
			label = FieldValueControl.getDataTypePlaceholder(dataType)
		}

		this.$('[name=' + this.field.id + '] .rollup-name')
			.toggleClass('placeholder', _.isNil(val) || val === '')
			.text(label)
	},

	getRollupValue() {
		return this.$('.rollup-selector').attr('value')
	},

	_maybePercentToDecimal: function(suggestion) {
		if (this.underlyingField.dataType === PERCENT &&
			typeof suggestion === 'string' && !_.isEmpty(suggestion)) {
				return moveDecimal(this._stripPercentSign(suggestion), -2)
		}

		return suggestion
	},

	_maybeDecimalToPercent: function(value) {
		if (this.underlyingField.dataType === PERCENT) {
			return formatValue({
				value,
				dataType: PERCENT,
				shorten: false
			})
		}
		return value
	},

	_val: function(newValue) {
		if (arguments.length) {
			this.fieldEl.val(
				this._stripPercentSign(this._maybeDecimalToPercent(newValue))
			)
		} else {
			let value = (this.markedField || this.fieldEl).val()
			value = _.isNil(value) ? '' : value
			return this._maybePercentToDecimal(value)
		}
	},

	renderSuggestions: function() {
		// create the select
		const selectOptions = this.getSuggestionOptions()
		if (_.isNil(selectOptions)) return

		this.fieldEl = this.$('[name=' + this.field.id + ']')
		this.fieldEl.prop('disabled', this.options.state === DISABLED)
		if (this.options.suggestedOnly) {
			selects(this.fieldEl, selectOptions)
		} else if (this.options.enableLazySelect) {
			// Ensure the selectOptions is in the correct format and is an array
			const optionsArray = Array.isArray(selectOptions.options) ?
				selectOptions.options : []
			const formattedSelectOptions = optionsArray.map(option => ({
				value: option.value,
				label: option.label,
				selected: option.selected ?? option.value == this.value
			}))
			// Create a wrapper div with the necessary classes and append
			// the fieldEl. These classes make a native select look like a
			// rich select
			const wrapper = $('<div class="select-wrap lazy-select small">')
			this.fieldEl.wrap(wrapper)
			let selectedOption = formattedSelectOptions.find(option => option.selected)
			// Set up the options for LazyFilteringSelect
			const lazySelectOptions = {
				el: this.fieldEl.parent(),
				getSelectOptions: () => formattedSelectOptions,
				selectedOption,
				filteringOptions: {
					combo: true
				}
			}
			this.lazySelect = new LazyFilteringSelect(lazySelectOptions).render()
			this.lazySelect.on('rendered', () => {
				this.markedField.calculateMarkerAndFieldWidth()
			})
		} else {
			selects.filtering(this.fieldEl, _.extend(selectOptions,
				{ combo: true }))
		}

		this.fieldEl.data('val', this._val.bind(this))
	},

	getSuggestionOptions: function() {
		// if there are suggestions, render a combo box (otherwise, it will
		// just be a plain INPUT box)
		var suggestions =
			FieldValueControl.formatSuggestions(_.get(
				this.options.suggestions,
				this.options.fieldId
			))
		if (_.isEmpty(suggestions) || this.multiline || this.textOnly) {
			return
		}
		// package the suggestions into a format the select and sifter like
		var suggestionObjects = _.map(suggestions, suggestion => {
			return this.getSuggestionOption(suggestion)
		})

		// If the currently selected value isn't in the suggestions, add an
		// option for it.
		// #53448 Note that suggestions are always Strings (they come from
		// FormWidgetSuggestion.value), but this.value could be another type
		// (like a number), so do a non-strict comparison
		let addOptionForCurrentValue = !_.isNil(this.value)
			&& !_.find(suggestionObjects, s => s.selected)
		if (addOptionForCurrentValue) {
			let option =  this.getSuggestionOption({
				value: this.value,
				label: this.value
			})
			option.selected = true
			suggestionObjects.push(option)
		}
		return { options: suggestionObjects, sort: [] }
	},

	getSuggestionOption (suggestion) {
		// #59258
		// Number data types are passed in the suggestion property as a simple
		// number rather than an object with a label and value property, so we
		// need to handle this case
		if (typeof suggestion === 'number') {
			suggestion = { value: suggestion, label: suggestion }
		}

		// if the dataType is percent, we will need to convert the suggestion
		// values from decimal to percentage
		let value =
			this._stripPercentSign(
				this._maybeDecimalToPercent(suggestion?.value)
			)

		if (this.underlyingField.dataType === CURRENCY) {
			value = formatValue({
				value,
				dataType: this.underlyingField.dataType,
				shorten: false,
				omitCurrencySymbol: true,
				group: false
			})
		}
		const label = this._stripPercentSign(formatValue({
			// #51640
			// Use the label if it exists, otherwise use the value. Blank/empty
			// values should be displayed as-is
			value: suggestion?.label ?? suggestion?.value,
			dataType: this.underlyingField.dataType,
			omitCurrencySymbol: true,
			shorten: false
		}))
		var object = { label, value }

		if (this.underlyingField.dataType === PERCENT) {
			// #59014 `value` has been converted from percent, so compare with
			// the original suggestion's value
			object.selected = this.value == suggestion?.value
		} else if (this.value == value) {
			object.selected = true
		}
		return object
	},

	getValidators: function() {
		let validators = this.options.validators || []
		if (this.required) {
			validators.push(requiredValidator)
		}
		if (!this.isDatasetLink() && isNumericDataType(this.underlyingField.dataType)) {
			validators.push(numericValidator)
		}
		return validators
	},

	undo: function() {
		// overwrite the previously user-specified value
		const data = {}
		let value
		const fieldId = this.options.fieldId
		const field = this.model.combinedFieldsById[fieldId]
		const isManualField = field.fieldType === 'manual'
		if (isNewRecord(this.record) || isManualField) {
			value = null
			data[fieldId] = value
		} else {
			// formHelper ignores undefined values so when there is no rawRecord
			// make sure the value is null so the field is cleared out
			value = _.get(this.record.rawRecord, this.fieldIndex)
			value = _.isUndefined(value) ? null : value
			data[this.options.fieldId] = value

			if (isGeographicOrRollup(this.underlyingField.dataType)) {
				this.setRollupValue({ value })
			}
		}

		this.value = value
		formHelper.writeToDom(data, this.el)
		this.$('.type-icon').remove() // prevent duping icons

		if (this.options.checkbox) {
			this.renderFormElement()
			this.$('input').prop('checked', this.value)
		} else {
			this.render()
			// if this is manual field, the previous value that was added to it will be in
			// suggestions, so we need to clear the suggestions too
		}

		this.toggleEdited(false)

		// #56960
		// Ensure widget actors are executed when undo button is pressed
		// This signal triggers the `onChangeWidgetValue` function in
		// field-widget.js
		this.change()

		_.result(this.$(`[name=${this.options.fieldId}]`).data('view'),
			'clearError')
		this.formView.change()
	},

	// toggle the edited text and undo button
	toggleEdited: function(edited) {
		this.$('.edited').toggleClass('is-not-displayed', !edited)
		this.$('.undo').prop('disabled', !edited)
		this.$('.edited-undo').data('edited', edited)
		if (!_.isNil(this.record.edited)) {
			this.record.edited[this.fieldIndex] = edited
		}
	},

	// callback when a select or input is changed.  toggles the appropriated
	// "edited" text and undo button
	modifyInput: function(e) {
		// #14099 only respond to input events on dates when a value has been
		// specified or else IE11 incorrectly marks the field as edited
		if (e.type == 'input' && _.isEmpty(this.$(e.target).val())) {
			return
		}
		this.toggleEdited(true)
		this.change()
	},

	selectTreeItem: function(e) {
		var treeId = this.underlyingField.treeId
		var button = this.$(e.target).closest('button')
		if (button.hasClass('is-disabled') ||
			button.attr('disabled') == 'disabled' ||
			_.get(this.options, 'state') === 'disabled') {
			return
		}

		var url = 'api/datasets/' + this.field.datasetId +
			'/field-values?fieldId=' + this.field.id

		let treeOptions = {
			treeId: treeId,
			selectedId: button.val(),
			showKeys: this.underlyingField.showKeys,
			// add ?keys=true to the end of the URL used to request tree items
			params: {
				keys: true
			}
		}

		// #48393 restrict tree selection when editing geographic fields
		let dataType = this.underlyingField.dataType
		if (isGeographic(dataType)) {
			// make sure we use a geographic level that works with the rollup tree
			let geographicLevel = dataTypeToTreeType(dataType)
			let fieldLabel = this.field.label
			// #63092 sort the tree items alphabetically for geographic fields (see #52312)
			let sortAlphabetically = true
			_.extend(treeOptions, { geographicLevel, fieldLabel, sortAlphabetically })
		}

		if (this.field.primaryKey) {
			appDisplay.dialog($.get(url), invalidIds => {
				if (!_.isEmpty(button.val())) {
					invalidIds = _.filter(invalidIds, id =>
						id != button.val())
				}
				return this.displayDatasetTreeItemSelector(e, _.extend({},
					treeOptions, { invalidIds }))
			})
		} else {
			appDisplay.dialog(this.displayDatasetTreeItemSelector(e, treeOptions))
		}

	},

	displayDatasetTreeItemSelector: function(e, opts={}) {
		var form = new DatasetTreeItemSelector(opts)
		form.on('select', (treeItem) => {
			form.trigger('done')
			this.options.treeItemsById[treeItem.id] = treeItem
			Backbone.trigger('load:dataset-tree-items', [treeItem])
			this.setRollupValue({ value: treeItem.id })
			this.markedField.validate()
			this.formView.change(e)
			this.modifyInput(e)
			this.toggleEdited(true)
		})
		return form
	},

	clearTreeItem: function(e) {
		if (this.isNotEditablePk()) return
		this.setRollupValue({ value: '' })
		this.modifyInput(e)
		this.formView.change(e)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.markedField, this.notEditablePkTooltip,
			this.lazySelect], 'remove')
	}

})

FieldValueControl.getDataTypePlaceholder = function(dataType) {
	let key = `datasets.records.fieldValueControl.placeholder.${dataType}`
	let label = i18n.get(key)

	return label
}

/**
 * #57960
 * Sometimes suggestions are only received as string values depending on
 * where they're passed in or can be missing a label.
 * We need to convert them to { value, label } objects for consistency.
 * @param suggestions - the suggestions to format
 * @returns {Array} - the formatted suggestions
 */
FieldValueControl.formatSuggestions = function(suggestions) {
	_.each(suggestions, (suggestion, index) => {
		if (_.isString(suggestion)) {
			suggestions[index] = { value: suggestion, label: suggestion }
		}

		suggestion.label = suggestion.label ?? suggestion.value
	})

	return suggestions
}

export default FieldValueControl
FieldValueControl.__filename__ = 'datasets/records/field-value-control.js'
